import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography,
} from '@mui/material';
import { accountingAPI, ledgerAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { RESOURCES, usePermissions } from '../../hooks/usePermissions';

const dateString = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const coverageGapLabels = {
  ledger_invoice_scan_capped: 'Ledger invoice scan reached its 10,000 document limit.',
  ledger_receipt_scan_capped: 'Ledger receipt scan reached its 10,000 document limit.',
  school_fee_invoice_source_scan_capped: 'SchoolSync invoice sources reached their 10,000 group limit.',
  school_fee_payment_source_scan_capped: 'SchoolSync payment sources reached their 10,000 receipt limit.',
  schoolpay_provider_scan_unavailable: 'SchoolPay transaction storage is unavailable for this tenant.',
  schoolpay_provider_scan_capped: 'SchoolPay transaction scan reached its 10,000 transaction limit.',
  exception_list_capped: 'The exception list reached its 500 item limit.',
  resolution_history_scan_capped: 'Resolution history reached its scan limit. Some recorded actions may not appear.',
};

const resolutionOptions = [
  { value: 'source_reviewed', label: 'SchoolSync source reviewed' },
  { value: 'ledger_reviewed', label: 'Ledger document reviewed' },
  { value: 'duplicate_reviewed', label: 'Duplicate reviewed' },
  { value: 'approved_difference', label: 'Difference reviewed and approved' },
  { value: 'other', label: 'Other handling documented' },
];

const SummaryTable = ({ title, rows, receipts = false, feeHeads = false }) => (
  <Box mt={2}>
    <Typography variant="subtitle2" fontWeight={700} gutterBottom>{title}</Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Term</TableCell>
          <TableCell>Fee group</TableCell>
          {feeHeads && <TableCell>Fee head</TableCell>}
          {receipts && <TableCell>Payment method</TableCell>}
          <TableCell align="right">Sources</TableCell>
          <TableCell align="right">Matched</TableCell>
          <TableCell align="right">Posted</TableCell>
          <TableCell align="right">SchoolSync</TableCell>
          <TableCell align="right">Ledger</TableCell>
          <TableCell align="right">Variance</TableCell>
          {receipts && <TableCell align="right">Unapplied</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {(rows || []).map((row) => (
          <TableRow key={`${row.term_id}-${row.fee_group_id}-${row.fee_type_id || ''}-${row.payment_method || ''}`}>
            <TableCell>{row.term || 'Unassigned'}</TableCell>
            <TableCell>{row.fee_group}</TableCell>
            {feeHeads && <TableCell>{row.fee_head}</TableCell>}
            {receipts && <TableCell>{row.payment_method}</TableCell>}
            <TableCell align="right">{row.source_count}</TableCell>
            <TableCell align="right">{row.matched_count}</TableCell>
            <TableCell align="right">{row.posted_count}</TableCell>
            <TableCell align="right">{formatCurrency(row.source_amount)}</TableCell>
            <TableCell align="right">{formatCurrency(row.ledger_amount)}</TableCell>
            <TableCell align="right" sx={{ color: Math.abs(Number(row.variance)) > 0.009 ? 'error.main' : 'success.main' }}>
              {formatCurrency(row.variance)}
            </TableCell>
            {receipts && <TableCell align="right" sx={{ color: Number(row.unapplied_amount) > 0.009 ? 'error.main' : 'text.secondary' }}>
              {formatCurrency(row.unapplied_amount)}{row.unapplied_count ? ` (${row.unapplied_count})` : ''}
            </TableCell>}
          </TableRow>
        ))}
        {!rows?.length && <TableRow><TableCell colSpan={receipts ? 10 : (feeHeads ? 9 : 8)}>No records in this period.</TableCell></TableRow>}
      </TableBody>
    </Table>
  </Box>
);

const FeeReconciliationReport = () => {
  const { canUpdate } = usePermissions();
  const canRecordResolutions = canUpdate(RESOURCES.ACCOUNTING.ACCOUNTS);
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [from, setFrom] = useState(dateString(monthStart));
  const [to, setTo] = useState(dateString(today));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ledgerInspection, setLedgerInspection] = useState({ open: false, loading: false, error: '', document: null, type: null, reference: null });
  const [sourceInspection, setSourceInspection] = useState({ open: false, loading: false, error: '', source: null, type: null, reference: null });
  const [resolution, setResolution] = useState({ open: false, item: null, code: 'source_reviewed', note: '', saving: false, error: '' });

  const loadReport = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await accountingAPI.getFeeReconciliation({ from, to });
      setReport(response.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load fee reconciliation.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const inspectLedgerDocument = async (type, sourceRef, documentId = null) => {
    const resource = type === 'invoice' ? 'invoices' : 'payments';
    const basePath = `accounts-receivable/${resource}`;
    const path = documentId
      ? `${basePath}/${documentId}`
      : `${basePath}/by-reference/${encodeURIComponent(sourceRef)}`;
    setLedgerInspection({ open: true, loading: true, error: '', document: null, type, reference: sourceRef });
    try {
      const response = await ledgerAPI.get('receivables', path);
      const payload = response.data;
      setLedgerInspection({
        open: true,
        loading: false,
        error: '',
        document: payload?.success === true ? payload.data : payload,
        type,
        reference: sourceRef,
      });
    } catch (err) {
      setLedgerInspection({
        open: true,
        loading: false,
        error: err?.response?.status === 404
          ? 'No ledger document matched this lookup.'
          : 'Could not inspect the ledger document. Check receivables access and ledger connectivity.',
        document: null,
        type,
        reference: sourceRef,
      });
    }
  };

  const inspectSchoolSource = async (item, page = 1, append = false) => {
    setSourceInspection((current) => append
      ? { ...current, open: true, loading: true, error: '' }
      : { open: true, loading: true, error: '', source: null, type: item.type, reference: item.source_ref });
    try {
      const response = await accountingAPI.getFeeReconciliationSource(item.type, item.source_ref, { page, per_page: 50 });
      const source = response.data;
      setSourceInspection((current) => ({
        ...current,
        open: true,
        loading: false,
        error: '',
        type: item.type,
        reference: item.source_ref,
        source: append && current.source
          ? {
              ...source,
              allocation_rows: [...(current.source.allocation_rows || []), ...(source.allocation_rows || [])],
            }
          : source,
      }));
    } catch (err) {
      setSourceInspection((current) => ({
        ...current,
        open: true,
        loading: false,
        error: err?.response?.status === 404
          ? 'No current SchoolSync source matched this reference.'
          : (err?.response?.data?.message || 'Could not inspect the SchoolSync source record.'),
      }));
    }
  };

  const openResolution = (item) => {
    setResolution({ open: true, item, code: 'source_reviewed', note: '', saving: false, error: '' });
  };

  const saveResolution = async () => {
    if (!resolution.item || resolution.note.trim().length < 10) return;
    setResolution((current) => ({ ...current, saving: true, error: '' }));
    try {
      const response = await accountingAPI.recordFeeExceptionResolution({
        from: report.period.from,
        to: report.period.to,
        type: resolution.item.type,
        source_ref: resolution.item.source_ref,
        reason: resolution.item.reason,
        fingerprint: resolution.item.fingerprint,
        resolution_code: resolution.code,
        note: resolution.note.trim(),
      });
      setReport((current) => current && ({
        ...current,
        exceptions: current.exceptions.map((item) => item.fingerprint === resolution.item.fingerprint
          ? { ...item, resolution: response.data.resolution }
          : item),
      }));
      setResolution({ open: false, item: null, code: 'source_reviewed', note: '', saving: false, error: '' });
    } catch (err) {
      setResolution((current) => ({
        ...current,
        saving: false,
        error: err?.response?.data?.message || 'Could not record resolution handling.',
      }));
    }
  };

  const inspectedDocument = ledgerInspection.document || {};
  const inspectedAmount = inspectedDocument.grand_total
    ?? inspectedDocument.amount_received
    ?? inspectedDocument.amount;
  const inspectedSource = sourceInspection.source || {};
  const inspectedStudent = inspectedSource.student || {};

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Fee source-to-ledger reconciliation</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Compares fee invoice groups and receipts to ledger documents using their stable source references.
        </Typography>
        <Box component="form" onSubmit={loadReport} display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField label="From" type="date" size="small" value={from} onChange={(event) => setFrom(event.target.value)} InputLabelProps={{ shrink: true }} required />
          <TextField label="To" type="date" size="small" value={to} onChange={(event) => setTo(event.target.value)} InputLabelProps={{ shrink: true }} required />
          <Button type="submit" variant="contained" disabled={loading || !from || !to}>
            {loading ? 'Reconciling...' : 'Run report'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {report && (
          <>
            <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
              <Chip color={report.complete ? 'success' : 'warning'} label={report.complete ? 'Source scan complete' : 'Source scan partial'} />
              <Chip label={`${report.source_counts.invoice_groups} invoice group(s)`} />
              <Chip label={`${report.source_counts.payments} receipt(s)`} />
              <Chip color={report.exceptions.length ? 'error' : 'success'} label={`${report.exceptions.length}${report.exceptions_truncated ? '+' : ''} exception(s)`} />
            </Box>
            {!report.complete && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {report.coverage_gaps?.map((gap) => coverageGapLabels[gap] || gap.split('_').join(' ')).join(' ')}
                {' '}Narrow the date range or restore the missing source before treating totals as complete.
              </Alert>
            )}
            {report.gl_reconciliation?.available ? (
              <Alert severity={report.gl_reconciliation.is_reconciled ? 'success' : 'error'} sx={{ mt: 2 }}>
                <b>Ledger AR control check ({report.gl_reconciliation.period_name || `period ${report.gl_reconciliation.period_id}`}):</b>{' '}
                subledger {formatCurrency(report.gl_reconciliation.subledger_total)}; GL control {formatCurrency(report.gl_reconciliation.gl_control_balance)}; difference {formatCurrency(report.gl_reconciliation.difference)}.
                {report.gl_reconciliation.control_account_code ? ` Control account ${report.gl_reconciliation.control_account_code}.` : ''}{' '}
                This is the whole ledger AR control balance, including non-fee receivables.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>{report.gl_reconciliation?.reason || 'Ledger AR control check is unavailable.'}</Alert>
            )}
            <SummaryTable title="Fee invoices by term and fee group" rows={report.invoice_summary} />
            <SummaryTable title="Fee invoices by fee head" rows={report.fee_head_summary} feeHeads />
            <SummaryTable title="Fee receipts by term, group, and payment method" rows={report.receipt_summary} receipts />
            {report.provider_reconciliation?.available ? (
              <Box mt={3}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>SchoolPay receipt matching</Typography>
                <Alert severity="info" sx={{ mb: 1 }}>
                  This compares provider receipts with local fee payments and ledger receipts. It does not reconcile SchoolPay settlements, provider fees, or bank deposits.
                </Alert>
                {report.provider_reconciliation.truncated && (
                  <Alert severity="warning" sx={{ mb: 1 }}>Provider scan reached its 10,000 transaction limit; results are partial.</Alert>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Payment channel</TableCell>
                        <TableCell>Settlement bank code</TableCell>
                        <TableCell>Provider status</TableCell>
                        <TableCell align="right">Provider receipts</TableCell>
                        <TableCell align="right">Provider amount</TableCell>
                        <TableCell align="right">Fee payments</TableCell>
                        <TableCell align="right">Fee amount</TableCell>
                        <TableCell align="right">Ledger posted</TableCell>
                        <TableCell align="right">Variance</TableCell>
                        <TableCell align="right">Ledger variance</TableCell>
                        <TableCell align="right">Unmatched</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.provider_reconciliation.summary_by_channel.map((row) => (
                        <TableRow key={`${row.payment_channel}-${row.settlement_bank_code}-${row.completion_status}`}>
                          <TableCell>{row.payment_channel}</TableCell>
                          <TableCell>{row.settlement_bank_code}</TableCell>
                          <TableCell>{row.completion_status}</TableCell>
                          <TableCell align="right">{row.provider_count}</TableCell>
                          <TableCell align="right">{formatCurrency(row.provider_amount)}</TableCell>
                          <TableCell align="right">{row.fee_payment_count}</TableCell>
                          <TableCell align="right">{formatCurrency(row.fee_payment_amount)}</TableCell>
                          <TableCell align="right">{row.ledger_posted_count} / {row.ledger_receipt_count}</TableCell>
                          <TableCell align="right" sx={{ color: Math.abs(Number(row.variance)) > 0.009 ? 'error.main' : 'success.main' }}>
                            {formatCurrency(row.variance)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: Math.abs(Number(row.ledger_variance)) > 0.009 ? 'error.main' : 'success.main' }}>
                            {formatCurrency(row.ledger_variance)}
                          </TableCell>
                          <TableCell align="right">{row.unmatched_count}</TableCell>
                        </TableRow>
                      ))}
                      {!report.provider_reconciliation.summary_by_channel.length && (
                        <TableRow><TableCell colSpan={11}>No SchoolPay school-fee transactions in this period.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {report.provider_reconciliation.exceptions.length > 0 && (
                  <>
                    <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                      Showing up to 500 provider receipt exceptions.
                    </Typography>
                    <TableContainer sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Provider receipt</TableCell>
                            <TableCell>Channel / bank</TableCell>
                            <TableCell>Issue</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Provider</TableCell>
                            <TableCell align="right">Fee payment</TableCell>
                            <TableCell align="right">Variance</TableCell>
                            <TableCell align="right">Ledger</TableCell>
                            <TableCell>Ledger receipt</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.provider_reconciliation.exceptions.map((item) => (
                            <TableRow key={item.provider_transaction_id || item.provider_receipt_number}>
                              <TableCell>{item.provider_receipt_number}</TableCell>
                              <TableCell>{item.payment_channel} / {item.settlement_bank_code}</TableCell>
                              <TableCell>{item.issues.join(', ').split('_').join(' ')}</TableCell>
                              <TableCell>{item.completion_status || '-'} / {item.match_status || '-'}</TableCell>
                              <TableCell align="right">{formatCurrency(item.provider_amount)}</TableCell>
                              <TableCell align="right">{item.fee_payment_amount === null ? '-' : formatCurrency(item.fee_payment_amount)}</TableCell>
                              <TableCell align="right">{item.variance === null ? '-' : formatCurrency(item.variance)}</TableCell>
                              <TableCell align="right">{item.ledger_amount === null ? '-' : formatCurrency(item.ledger_amount)}</TableCell>
                          <TableCell>
                            {item.ledger_document_id && (
                              <Button size="small" onClick={() => inspectLedgerDocument('receipt', item.ledger_receipt_ref, item.ledger_document_id)}>
                                Inspect #{item.ledger_document_id}
                              </Button>
                            )}
                            <Typography variant="caption" display="block">
                              {item.ledger_receipt_ref || '-'}{item.ledger_status ? ` (${item.ledger_status})` : ''}
                            </Typography>
                          </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            ) : report.provider_reconciliation?.reason ? (
              <Alert severity="info" sx={{ mt: 2 }}>{report.provider_reconciliation.reason}</Alert>
            ) : null}
            <Typography variant="subtitle2" fontWeight={700} mt={3} mb={1}>Exceptions</Typography>
            {report.exceptions.length > 0 && !report.resolution_history_available && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Resolution notes are unavailable until the tenant accounting audit migration is deployed.
              </Alert>
            )}
            {report.exceptions.length ? (
              <Table size="small">
                <TableHead>
                    <TableRow>
                      <TableCell>Document</TableCell>
                      <TableCell>Fee head</TableCell>
                    <TableCell>Source reference</TableCell>
                    <TableCell>Issue</TableCell>
                    <TableCell>Ledger status</TableCell>
                    <TableCell>SchoolSync inspection</TableCell>
                    <TableCell>Ledger inspection</TableCell>
                    <TableCell>Resolution handling</TableCell>
                    <TableCell align="right">SchoolSync</TableCell>
                    <TableCell align="right">Ledger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.exceptions.map((item) => (
                    <TableRow key={`${item.type}-${item.source_ref}-${item.reason}-${item.fee_type_id || item.fee_head || ''}`}>
                      <TableCell>{item.type}</TableCell>
                      <TableCell>{item.fee_head || '-'}</TableCell>
                      <TableCell>{item.source_ref}</TableCell>
                      <TableCell>{item.reason.split('_').join(' ')}</TableCell>
                      <TableCell>{item.ledger_status || '-'}</TableCell>
                      <TableCell>
                        {item.school_source ? (
                          <Button size="small" onClick={() => inspectSchoolSource(item)}>Inspect source</Button>
                        ) : 'No local match'}
                      </TableCell>
                      <TableCell>
                        {item.ledger_document_ids?.length ? item.ledger_document_ids.map((id) => (
                          <Button key={id} size="small" onClick={() => inspectLedgerDocument(item.type, item.source_ref, id)}>
                            Inspect #{id}
                          </Button>
                        )) : item.ledger_document_id ? (
                          <Button size="small" onClick={() => inspectLedgerDocument(item.type, item.source_ref, item.ledger_document_id)}>
                            Inspect #{item.ledger_document_id}
                          </Button>
                        ) : (
                          <Button size="small" onClick={() => inspectLedgerDocument(item.type, item.source_ref)}>
                            Lookup by source
                          </Button>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220, maxWidth: 320 }}>
                        {item.resolution ? (
                          <>
                            <Chip size="small" color="info" label="Handling recorded" />
                            <Typography variant="caption" display="block" mt={0.5}>
                              {resolutionOptions.find((option) => option.value === item.resolution.code)?.label || item.resolution.code}
                              {item.resolution.user_name ? ` · ${item.resolution.user_name}` : ''}
                              {item.resolution.recorded_at ? ` · ${new Date(item.resolution.recorded_at).toLocaleString()}` : ''}
                            </Typography>
                            {item.resolution.note && <Typography variant="caption" display="block">{item.resolution.note}</Typography>}
                            {canRecordResolutions && report.resolution_history_available && <Button size="small" onClick={() => openResolution(item)}>Add note</Button>}
                          </>
                        ) : canRecordResolutions && report.resolution_history_available ? (
                          <Button size="small" onClick={() => openResolution(item)}>Record handling</Button>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">{item.source_amount === null ? '-' : formatCurrency(item.source_amount)}</TableCell>
                      <TableCell align="right">{item.ledger_amount === null ? '-' : formatCurrency(item.ledger_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="success.main">No source-reference exceptions were found in this period.</Typography>
            )}
            <Typography variant="caption" display="block" color="text.secondary" mt={1}>
              This report compares source documents and AR documents, including unapplied receipt amounts. It does not reconcile cash settlements, GL control accounts, or voids outside the selected dates.
            </Typography>
          </>
        )}
        <Dialog open={ledgerInspection.open} disableEscapeKeyDown={ledgerInspection.loading} onClose={() => {
          if (!ledgerInspection.loading) setLedgerInspection((current) => ({ ...current, open: false }));
        }} maxWidth='md' fullWidth>
          <DialogTitle>Ledger {ledgerInspection.type === 'invoice' ? 'invoice' : 'receipt'} inspection</DialogTitle>
          <DialogContent dividers>
            {ledgerInspection.loading ? (
              <Box display='flex' justifyContent='center' py={4}><CircularProgress size={28} /></Box>
            ) : ledgerInspection.error ? (
              <Alert severity='warning'>{ledgerInspection.error}</Alert>
            ) : ledgerInspection.document ? (
              <>
                <Table size='small'>
                  <TableBody>
                    <TableRow><TableCell>Document number</TableCell><TableCell>{inspectedDocument.invoice_number || inspectedDocument.payment_number || '-'}</TableCell></TableRow>
                    <TableRow><TableCell>Source reference</TableCell><TableCell>{inspectedDocument.reference || ledgerInspection.reference || '-'}</TableCell></TableRow>
                    <TableRow><TableCell>Status</TableCell><TableCell>{inspectedDocument.status || '-'}</TableCell></TableRow>
                    <TableRow><TableCell>Date</TableCell><TableCell>{inspectedDocument.invoice_date || inspectedDocument.payment_date || '-'}</TableCell></TableRow>
                    <TableRow><TableCell>Amount</TableCell><TableCell>{inspectedAmount === undefined ? '-' : formatCurrency(inspectedAmount)}</TableCell></TableRow>
                    {inspectedDocument.balance_due !== undefined && (
                      <TableRow><TableCell>Balance due</TableCell><TableCell>{formatCurrency(inspectedDocument.balance_due)}</TableCell></TableRow>
                    )}
                    <TableRow><TableCell>Customer</TableCell><TableCell>{inspectedDocument.customer?.customer_name || inspectedDocument.customer?.name || '-'}</TableCell></TableRow>
                    <TableRow><TableCell>Journal entry</TableCell><TableCell>{inspectedDocument.journalEntry?.entry_number || '-'}</TableCell></TableRow>
                  </TableBody>
                </Table>
                {!!inspectedDocument.items?.length && (
                  <>
                    <Typography variant='subtitle2' fontWeight={700} mt={2} mb={1}>Invoice lines</Typography>
                    <Table size='small'>
                      <TableHead><TableRow><TableCell>Line</TableCell><TableCell>Revenue account</TableCell><TableCell align='right'>Quantity</TableCell><TableCell align='right'>Unit price</TableCell><TableCell align='right'>Total</TableCell></TableRow></TableHead>
                      <TableBody>
                        {inspectedDocument.items.map((line, index) => (
                          <TableRow key={line.item_id || line.id || index}>
                            <TableCell>{line.item_name || line.description || '-'}</TableCell>
                            <TableCell>{line.revenueAccount?.account_code || line.revenueAccount?.account_name || '-'}</TableCell>
                            <TableCell align='right'>{line.quantity ?? '-'}</TableCell>
                            <TableCell align='right'>{line.unit_price === undefined ? '-' : formatCurrency(line.unit_price)}</TableCell>
                            <TableCell align='right'>{line.total_amount === undefined && line.line_total === undefined ? '-' : formatCurrency(line.total_amount ?? line.line_total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
                {!!inspectedDocument.allocations?.length && (
                  <>
                    <Typography variant='subtitle2' fontWeight={700} mt={2} mb={1}>Receipt allocations</Typography>
                    <Table size='small'>
                      <TableHead><TableRow><TableCell>Invoice</TableCell><TableCell>Status</TableCell><TableCell align='right'>Applied</TableCell></TableRow></TableHead>
                      <TableBody>
                        {inspectedDocument.allocations.map((allocation, index) => (
                          <TableRow key={allocation.allocation_id || allocation.id || index}>
                            <TableCell>{allocation.invoice?.invoice_number || '-'}</TableCell>
                            <TableCell>{allocation.invoice?.status || '-'}</TableCell>
                            <TableCell align='right'>{formatCurrency(allocation.allocated_amount || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </>
            ) : (
              <Alert severity='info'>No ledger document matched this source reference.</Alert>
            )}
          </DialogContent>
          <DialogActions><Button disabled={ledgerInspection.loading} onClick={() => setLedgerInspection((current) => ({ ...current, open: false }))}>Close</Button></DialogActions>
        </Dialog>
        <Dialog open={sourceInspection.open} onClose={() => {
          if (!sourceInspection.loading) setSourceInspection((current) => ({ ...current, open: false }));
        }} maxWidth="md" fullWidth>
          <DialogTitle>SchoolSync {sourceInspection.type === 'invoice' ? 'fee invoice source' : 'fee payment source'}</DialogTitle>
          <DialogContent dividers>
            {sourceInspection.loading && !sourceInspection.source ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : sourceInspection.error ? (
              <Alert severity="warning">{sourceInspection.error}</Alert>
            ) : sourceInspection.source ? (
              <>
                <Table size="small">
                  <TableBody>
                    <TableRow><TableCell>Source reference</TableCell><TableCell>{inspectedSource.source_ref || sourceInspection.reference}</TableCell></TableRow>
                    <TableRow><TableCell>Local record</TableCell><TableCell>{inspectedSource.type === 'invoice_group' ? `${inspectedSource.allocation_count} fee allocation(s)` : `Payment #${inspectedSource.payment_id}`}</TableCell></TableRow>
                    <TableRow><TableCell>Student</TableCell><TableCell>{[inspectedStudent.name, inspectedStudent.registration_no ? `(${inspectedStudent.registration_no})` : null].filter(Boolean).join(' ') || `Student #${inspectedStudent.id || '-'}`}</TableCell></TableRow>
                    <TableRow><TableCell>Term / fee group</TableCell><TableCell>{[inspectedSource.term, inspectedSource.fee_group].filter(Boolean).join(' / ') || '-'}</TableCell></TableRow>
                    <TableRow><TableCell>SchoolSync amount</TableCell><TableCell>{formatCurrency(inspectedSource.amount || 0)}</TableCell></TableRow>
                    {inspectedSource.type === 'receipt' && <>
                      <TableRow><TableCell>Payment date / method</TableCell><TableCell>{[inspectedSource.payment_date, inspectedSource.payment_method].filter(Boolean).join(' / ') || '-'}</TableCell></TableRow>
                      <TableRow><TableCell>Discount / fine</TableCell><TableCell>{formatCurrency(inspectedSource.discount || 0)} / {formatCurrency(inspectedSource.fine || 0)}</TableCell></TableRow>
                      {inspectedSource.provider_receipt_number && <TableRow><TableCell>Provider receipt</TableCell><TableCell>{inspectedSource.provider_receipt_number}</TableCell></TableRow>}
                      {inspectedSource.ledger_receipt_id && <TableRow><TableCell>Linked ledger receipt</TableCell><TableCell>#{inspectedSource.ledger_receipt_id}</TableCell></TableRow>}
                    </>}
                  </TableBody>
                </Table>
                {inspectedSource.type === 'receipt' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Discount and fine values are local fee fields shown for review; the current ledger receipt flow posts the payment amount only.
                  </Alert>
                )}
                {inspectedSource.type === 'invoice_group' && !!inspectedSource.allocation_rows?.length && <>
                  <Typography variant="subtitle2" fontWeight={700} mt={2} mb={1}>Fee allocations</Typography>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Allocation</TableCell><TableCell>Fee head</TableCell><TableCell>Due date</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                    <TableBody>{inspectedSource.allocation_rows.map((row) => (
                      <TableRow key={row.id}><TableCell>#{row.id}</TableCell><TableCell>{row.fee_head || `Fee type #${row.fee_type_id}`}</TableCell><TableCell>{row.due_date || '-'}</TableCell><TableCell align="right">{formatCurrency(row.amount)}</TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                  {inspectedSource.pagination?.has_more && (
                    <Button size="small" sx={{ mt: 1 }} disabled={sourceInspection.loading} onClick={() => inspectSchoolSource({ type: 'invoice', source_ref: sourceInspection.reference }, inspectedSource.pagination.current_page + 1, true)}>
                      {sourceInspection.loading ? 'Loading...' : 'Load more allocations'}
                    </Button>
                  )}
                </>}
              </>
            ) : null}
          </DialogContent>
          <DialogActions><Button disabled={sourceInspection.loading} onClick={() => setSourceInspection((current) => ({ ...current, open: false }))}>Close</Button></DialogActions>
        </Dialog>
        <Dialog open={resolution.open} onClose={() => {
          if (!resolution.saving) setResolution((current) => ({ ...current, open: false }));
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Record fee exception handling</DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              This saves an audit note for the current exception. It does not change SchoolSync or ledger records, and the exception remains visible until a later report no longer detects it.
            </Alert>
            {resolution.item && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {resolution.item.type} · {resolution.item.source_ref} · {resolution.item.reason.split('_').join(' ')}
              </Typography>
            )}
            <TextField
              select
              fullWidth
              label="Handling category"
              margin="normal"
              value={resolution.code}
              onChange={(event) => setResolution((current) => ({ ...current, code: event.target.value }))}
              disabled={resolution.saving}
            >
              {resolutionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Handling note"
              value={resolution.note}
              onChange={(event) => setResolution((current) => ({ ...current, note: event.target.value.slice(0, 1000) }))}
              helperText="Required, 10–1000 characters. Do not include student personal data."
              disabled={resolution.saving}
            />
            {resolution.error && <Alert severity="error" sx={{ mt: 2 }}>{resolution.error}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button disabled={resolution.saving} onClick={() => setResolution((current) => ({ ...current, open: false }))}>Cancel</Button>
            <Button
              variant="contained"
              disabled={resolution.saving || resolution.note.trim().length < 10}
              onClick={saveResolution}
            >
              {resolution.saving ? 'Saving...' : 'Save audit note'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default FeeReconciliationReport;
